import { useEffect, useMemo, useState } from 'react';
import {
    query,
    where,
} from 'firebase/firestore';
import { useOutletContext } from 'react-router-dom';
import useTodoCollection from 'data/firestore/agent/todo/useCollection';
import onSnapshotTodoCollection from 'data/firestore/agent/todo/onSnapshotCollection';
import { Todo } from 'data/firestore/agent/todo/types';
import { EntityGet } from 'data/firestore/types';
import { ViewContextType } from 'pages/Opportunities/types';

type TodosByOpportunityId = Record<string, EntityGet<Todo>[]>;

const getDueAtRange = (todoDueAt?: string | null) => {
    if (!todoDueAt) {
        return { from: null, to: null };
    }

    const [fromRaw, toRaw] = String(todoDueAt).split(',');
    const from = fromRaw ? new Date(fromRaw) : null;
    const to = toRaw ? new Date(toRaw) : null;

    return {
        from: from && !Number.isNaN(from.getTime()) ? from : null,
        to: to && !Number.isNaN(to.getTime()) ? to : null,
    };
};

const getTodoDate = (dueDate?: Date | string | null) => {
    if (!dueDate) return null;
    const parsed = dueDate instanceof Date ? dueDate : new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
};

const getTodoTimestamp = (todo: EntityGet<Todo>) => (
    getTodoDate(todo.dueDate as Date | string | null)?.getTime() || Number.MAX_SAFE_INTEGER
);

const isOverdueTodo = (todo: EntityGet<Todo>) => {
    if (todo.status === 'done' || todo.status === 'cancelled') {
        return false;
    }

    const dueDate = getTodoDate(todo.dueDate as Date | string | null);
    if (!dueDate) {
        return false;
    }

    return dueDate.getTime() <= Date.now();
};

const applyTodoFilters = ({
    todos,
    assigneeFilter,
    priorityFilter,
    statusFilter,
    dueAtFilter,
}: {
    todos: EntityGet<Todo>[];
    assigneeFilter: string[];
    priorityFilter: string[];
    statusFilter: string[];
    dueAtFilter: { from: Date | null; to: Date | null };
}) => todos.filter((todo) => {
    if (assigneeFilter.length > 0) {
        const assigneeId = todo.assignedTo?.uid;
        if (!assigneeId || !assigneeFilter.includes(assigneeId)) {
            return false;
        }
    }

    if (priorityFilter.length > 0) {
        if (!todo.priority || !priorityFilter.includes(todo.priority)) {
            return false;
        }
    }

    if (statusFilter.length > 0) {
        if (!todo.status || !statusFilter.includes(todo.status)) {
            return false;
        }
    }

    if (dueAtFilter.from || dueAtFilter.to) {
        const dueDate = getTodoDate(todo.dueDate as Date | string | null);
        if (!dueDate) {
            return false;
        }
        if (dueAtFilter.from && dueDate < dueAtFilter.from) {
            return false;
        }
        if (dueAtFilter.to && dueDate > dueAtFilter.to) {
            return false;
        }
    }

    return true;
});

const useOpportunitiesTodosViewData = ({
    agentId,
}: {
    agentId: string;
}) => {
    const {
        selectedPipeline = '',
        filters,
    } = useOutletContext<ViewContextType>();

    const assigneeFilter = useMemo(
        () => (filters.todoAssignee ? String(filters.todoAssignee).split(',').filter(Boolean) : []),
        [filters.todoAssignee],
    );
    const priorityFilter = useMemo(
        () => (filters.todoPriority ? String(filters.todoPriority).split(',').filter(Boolean) : []),
        [filters.todoPriority],
    );
    const statusFilter = useMemo(
        () => (filters.todoStatus ? String(filters.todoStatus).split(',').filter(Boolean) : []),
        [filters.todoStatus],
    );
    const dueAtFilter = useMemo(
        () => getDueAtRange(filters.todoDueAt as string | null | undefined),
        [filters.todoDueAt],
    );

    console.log(assigneeFilter, priorityFilter, statusFilter, dueAtFilter);

    const {
        data: pipelineTodos = [],
        isLoading: isLoadingMatchedTodos,
        error: matchedTodosError,
    } = useTodoCollection(agentId, {
        listQueryFn: (ref) => query(
            ref,
            where('pipelineId', '==', selectedPipeline),
            where('status', '!=', 'cancelled'),
        ),
        disabled: !agentId || !selectedPipeline,
        track: [agentId, selectedPipeline],
    });

    useEffect(() => {
        if (!matchedTodosError) return;
        // Useful for Firestore index debugging in local development.
        // eslint-disable-next-line no-console
        console.error('[Opportunities Todos View] useTodoCollection failed', {
            error: matchedTodosError,
            agentId,
            selectedPipeline,
            filters,
        });
    }, [matchedTodosError, agentId, selectedPipeline, filters]);

    const matchedTodos = useMemo(
        () => applyTodoFilters({
            todos: pipelineTodos,
            assigneeFilter,
            priorityFilter,
            statusFilter,
            dueAtFilter,
        }),
        [pipelineTodos, assigneeFilter, priorityFilter, statusFilter, dueAtFilter],
    );

    const overdueTodos = useMemo(
        () => pipelineTodos
            .filter(isOverdueTodo)
            .sort((a, b) => getTodoTimestamp(a) - getTodoTimestamp(b)),
        [pipelineTodos],
    );

    const opportunityIds = useMemo(
        () => {
            const latestByOpportunityId = matchedTodos.reduce(
                (acc, todo) => {
                    const current = acc[todo.opportunityId];
                    const updatedAt = todo.updatedAt?.getTime?.()
                        || todo.createdAt?.getTime?.()
                        || 0;
                    const currentUpdatedAt = current?.updatedAt || 0;
                    if (!current || updatedAt > currentUpdatedAt) {
                        acc[todo.opportunityId] = {
                            updatedAt,
                        };
                    }
                    return acc;
                },
                {} as Record<string, { updatedAt: number }>,
            );

            return Object.entries(latestByOpportunityId)
                .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
                .map(([opportunityId]) => opportunityId);
        },
        [matchedTodos],
    );

    const [todosByOpportunityId, setTodosByOpportunityId] = useState<TodosByOpportunityId>({});
    const [isLoadingFullTodos, setIsLoadingFullTodos] = useState(false);

    useEffect(() => {
        if (!agentId || opportunityIds.length === 0) {
            setTodosByOpportunityId({});
            setIsLoadingFullTodos(false);
            return undefined;
        }

        setIsLoadingFullTodos(true);
        const loadingByOpportunityId: Record<string, boolean> = {};
        const unsubscribeList = opportunityIds.map((opportunityId) => {
            loadingByOpportunityId[opportunityId] = true;

            return onSnapshotTodoCollection(
                agentId,
                {
                    listQueryFn: (ref) => query(
                        ref,
                        where('opportunityId', '==', opportunityId),
                        where('status', '!=', 'cancelled'),
                    ),
                },
                (todos) => {
                    loadingByOpportunityId[opportunityId] = false;
                    setTodosByOpportunityId((prev) => ({
                        ...prev,
                        [opportunityId]: todos,
                    }));
                    setIsLoadingFullTodos(Object.values(loadingByOpportunityId).some(Boolean));
                },
                () => {
                    loadingByOpportunityId[opportunityId] = false;
                    setIsLoadingFullTodos(Object.values(loadingByOpportunityId).some(Boolean));
                },
            );
        });

        return () => {
            unsubscribeList.forEach((unsubscribe) => unsubscribe());
        };
    }, [agentId, opportunityIds]);

    const cardsData = useMemo(
        () => opportunityIds.map((opportunityId) => ({
            opportunityId,
            todos: todosByOpportunityId[opportunityId] || [],
        })),
        [opportunityIds, todosByOpportunityId],
    );

    return {
        cardsData,
        matchedTodosCount: matchedTodos.length,
        overdueTodos,
        overdueTodosCount: overdueTodos.length,
        isLoading: isLoadingMatchedTodos || isLoadingFullTodos,
    };
};

export default useOpportunitiesTodosViewData;
